import React from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../../../utils';
import { AlertTriangle, CheckCircle, XCircle, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import { Completeness } from '../../../pages/debtor/DocumentValidation';

interface Debtor {
  id: number;
  full_name: string;
  cif?: string;
}

interface DocumentValidationListProps {
  debtors: Debtor[];
  loading: boolean;
  getCompleteness: (debtorId: number) => Completeness;
}

export default function DocumentValidationList({
  debtors,
  loading,
  getCompleteness,
}: DocumentValidationListProps) {
  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-4 border-b">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Debitur dengan Dokumen Tidak Lengkap
        </h3>
      </div>
      <div className="p-4">
        {loading ? (
          <div className="space-y-4">
            {Array(3)
              .fill(0)
              .map((_, i) => (
                <div key={i} className="h-24 w-full bg-gray-200 rounded animate-pulse" />
              ))}
          </div>
        ) : debtors.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
            <p className="text-gray-500">Semua debitur memiliki dokumen lengkap</p>
          </div>
        ) : (
          <div className="space-y-4">
            {debtors.map((debtor, index) => {
              const completeness = getCompleteness(debtor.id);
              return (
                <motion.div
                  key={debtor.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                        <span className="font-semibold text-amber-700 text-lg">
                          {debtor.full_name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold">{debtor.full_name}</h3>
                        <p className="text-sm text-gray-500">CIF: {debtor.cif || '-'}</p>
                      </div>
                    </div>
                    <div className="flex-1 max-w-md">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-500">Kelengkapan</span>
                        <span
                          className={`text-sm font-medium ${
                            completeness.percentage >= 80
                              ? 'text-green-600'
                              : completeness.percentage >= 50
                                ? 'text-amber-600'
                                : 'text-red-600'
                          }`}
                        >
                          {completeness.complete}/{completeness.total} ({completeness.percentage}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            completeness.percentage >= 80
                              ? 'bg-green-500'
                              : completeness.percentage >= 50
                                ? 'bg-amber-500'
                                : 'bg-red-500'
                          }`}
                          style={{ width: `${completeness.percentage}%` }}
                        />
                      </div>
                    </div>
                    <Link to={createPageUrl(`DebtorDetail?id=${debtor.id}`)}>
                      <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm transition-colors">
                        <Eye className="w-4 h-4" /> Detail
                      </button>
                    </Link>
                  </div>
                  {completeness.missing.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-gray-500 mb-2">Dokumen yang belum ada:</p>
                      <div className="flex flex-wrap gap-2">
                        {completeness.missing.map(doc => (
                          <span
                            key={doc.type}
                            className="px-2 py-1 bg-red-50 text-red-700 border border-red-200 rounded-full text-xs flex items-center gap-1"
                          >
                            <XCircle className="w-3 h-3" />
                            {doc.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
